import { useEffect, useState, useCallback } from 'react';
import { Bug, X, Trash, WarningCircle, Warning, Info } from '@phosphor-icons/react';
import {
  subscribe, clearLog, markRead, getUnreadCount,
} from '@/lib/logger';

const levelStyles = {
  error: { bg: '#FEE2E2', accent: '#DC2626', label: 'ERROR', Icon: WarningCircle },
  warn:  { bg: '#FEF3C7', accent: '#D97706', label: 'WARN',  Icon: Warning },
  info:  { bg: '#DBEAFE', accent: '#2563EB', label: 'INFO',  Icon: Info },
};

/**
 * Floating, dismissable error-log tray. Always available, but invisible until something is logged.
 *
 * Behaviour:
 *  - Bell icon docked to the bottom-right corner. Badge shows unread count.
 *  - Click → slide-up panel with the last 100 entries (newest on top).
 *  - "Mark read" zeroes the badge; "Clear" wipes the buffer.
 */
export const ErrorLogPanel = () => {
  const [entries, setEntries] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const unsub = subscribe((log) => {
      setEntries([...log].reverse()); // newest first
      setUnread(getUnreadCount());
    });
    return unsub;
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
    markRead();
    setUnread(0);
  }, []);

  if (!entries.length && !open) return null;

  return (
    <>
      {!open && (
        <button
          data-testid="error-log-toggle"
          onClick={handleOpen}
          title={unread > 0 ? `${unread} new event${unread === 1 ? '' : 's'}` : `${entries.length} event${entries.length === 1 ? '' : 's'} logged`}
          className="fixed bottom-20 right-4 z-50 p-3 bg-[#0F3622] border-2 border-[#A3E635] rounded-full shadow-[3px_3px_0px_0px_rgba(163,230,53,0.5)] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(163,230,53,0.7)] transition-all"
        >
          <Bug size={22} weight="duotone" className="text-[#A3E635]" />
          {unread > 0 && (
            <span
              data-testid="error-log-badge"
              className="absolute -top-1 -right-1 bg-[#F87171] text-white text-[10px] font-black min-w-[20px] h-[20px] px-1 rounded-full flex items-center justify-center border-2 border-[#0B2818]"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          data-testid="error-log-panel"
          className="fixed bottom-20 right-4 z-50 w-[420px] max-w-[calc(100vw-2rem)] bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[6px_6px_0px_0px_rgba(163,230,53,0.4)] flex flex-col max-h-[60vh]"
        >
          <div className="flex items-center justify-between p-3 border-b-2 border-[#143E29]">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Bug size={18} weight="duotone" className="text-[#A3E635]" />
              Event Log ({entries.length})
            </h3>
            <div className="flex gap-1">
              <button
                data-testid="error-log-clear"
                onClick={() => clearLog()}
                title="Clear log"
                className="p-1.5 bg-[#F87171] text-white border-2 border-black rounded hover:-translate-y-0.5 transition-all"
              >
                <Trash size={14} weight="bold" />
              </button>
              <button
                data-testid="error-log-close"
                onClick={() => setOpen(false)}
                title="Close"
                className="p-1.5 bg-white text-black border-2 border-black rounded hover:-translate-y-0.5 transition-all"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto p-2 space-y-2">
            {entries.length === 0 ? (
              <p className="text-xs text-white text-center py-4 italic">No events logged.</p>
            ) : entries.map((e) => {
              const s = levelStyles[e.level] || levelStyles.info;
              const Icon = s.Icon;
              return (
                <div
                  key={e.id}
                  data-testid={`error-log-entry-${e.level}`}
                  className="rounded-md p-2 text-xs"
                  style={{ background: s.bg, border: `2px solid ${s.accent}` }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon size={14} weight="fill" style={{ color: s.accent }} />
                    <span className="font-black uppercase tracking-wider" style={{ color: s.accent }}>{s.label}</span>
                    <span className="font-bold text-black">· {e.label}</span>
                    <span className="ml-auto text-[10px] text-black opacity-60">{new Date(e.ts).toLocaleTimeString()}</span>
                  </div>
                  <div className="text-black font-medium break-words">{e.message || '—'}</div>
                  {e.stack && (
                    <details className="mt-1">
                      <summary className="text-[10px] text-black opacity-60 cursor-pointer">stack</summary>
                      <pre className="text-[10px] text-black opacity-70 whitespace-pre-wrap mt-1">{e.stack}</pre>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
